/**
 * Training Type Definitions
 * Represents halal training courses and certifications (Mock-up in Sprint 1)
 */

/**
 * Training course categories
 */
export enum TrainingCategory {
  HALAL_AWARENESS = 'HALAL_AWARENESS',
  HALAL_EXECUTIVE = 'HALAL_EXECUTIVE',
  HALAL_SUPERVISOR = 'HALAL_SUPERVISOR',
  INTERNAL_AUDITOR = 'INTERNAL_AUDITOR',
  SLAUGHTERING = 'SLAUGHTERING',
  SPECIALTY = 'SPECIALTY',
}

/**
 * Training status
 */
export enum TrainingStatus {
  UPCOMING = 'UPCOMING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/**
 * Enrollment status
 */
export enum EnrollmentStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

/**
 * Delivery method
 */
export enum DeliveryMethod {
  IN_PERSON = 'IN_PERSON',
  ONLINE = 'ONLINE',
  HYBRID = 'HYBRID',
}

/**
 * Training course
 */
export interface TrainingCourse {
  id: string;
  title: string;
  category: TrainingCategory;
  description: string;
  objectives: string[];

  // Delivery
  deliveryMethod: DeliveryMethod;
  durationHours: number;
  language: string; // e.g., "English", "Malay"

  // Prerequisites
  prerequisites?: string[];
  targetAudience: string;

  // Content
  syllabus: string[];
  materials?: string[]; // URLs to course materials

  // Certification
  certificateIssued: boolean;
  certificateValidityYears?: number;

  // Provider
  providerId?: string; // Organization providing the course
  providerName?: string;
  instructors: string[]; // List of instructor names

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Training session (specific scheduled instance of a course)
 */
export interface TrainingSession {
  id: string;
  courseId: string;
  courseName: string;
  status: TrainingStatus;

  // Schedule
  startDate: Date;
  endDate: Date;
  location?: string; // Physical location or "Online"
  meetingLink?: string; // For online sessions

  // Capacity
  maxParticipants: number;
  enrolledCount: number;
  availableSeats: number;

  // Instructor
  instructorId?: string;
  instructorName: string;

  // Fee (Sprint 1: mock-up only)
  fee?: number;
  currency?: string;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Training enrollment
 */
export interface TrainingEnrollment {
  id: string;
  sessionId: string;
  courseId: string;
  courseName: string;

  // Participant
  userId: string;
  userName: string;
  userEmail: string;
  companyId?: string;
  companyName?: string;

  // Enrollment
  enrolledAt: Date;
  status: EnrollmentStatus;

  // Attendance
  attended: boolean;
  attendanceMarkedAt?: Date;

  // Assessment
  assessmentScore?: number; // 0-100
  passed?: boolean;
  completedAt?: Date;

  // Certificate
  certificateId?: string;
  certificateIssuedAt?: Date;
  certificateUrl?: string;

  // Metadata
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Training certificate
 */
export interface TrainingCertificate {
  id: string;
  certificateNumber: string;
  courseId: string;
  courseName: string;
  userId: string;
  userName: string;

  // Issuance
  issuedAt: Date;
  issuedBy: string;
  expiryDate?: Date;

  // Certificate data
  score?: number;
  certificateUrl: string;

  // Metadata
  createdAt: Date;
}

/**
 * Training statistics
 */
export interface TrainingStatistics {
  totalCourses: number;
  totalSessions: number;
  totalEnrollments: number;
  totalCertificatesIssued: number;
  averageScore: number;
  completionRate: number; // Percentage
}

/**
 * Helper to check if certificate is valid
 */
export function isCertificateValid(certificate: TrainingCertificate): boolean {
  if (!certificate.expiryDate) return true; // No expiry
  return new Date(certificate.expiryDate) > new Date();
}

/**
 * Helper to calculate available seats
 */
export function getAvailableSeats(session: TrainingSession): number {
  return Math.max(0, session.maxParticipants - session.enrolledCount);
}

/**
 * Helper to check if session is full
 */
export function isSessionFull(session: TrainingSession): boolean {
  return session.enrolledCount >= session.maxParticipants;
}

/**
 * Helper to check if user can enroll
 */
export function canEnroll(session: TrainingSession): boolean {
  return (
    session.status === TrainingStatus.UPCOMING &&
    !isSessionFull(session) &&
    new Date(session.startDate) > new Date()
  );
}
